import { FormattedMessage } from 'react-intl';

// project imports
import { buildEntityTypeHierarchy } from 'store/app/workmgmt/entity-type-tree';

const createBasicPermissionChildren = (prefix, { includeCreateWorkOrder = false } = {}) => [
    { value: `${prefix}-canAdd`, label: <FormattedMessage id="authkeys-permission-canadd" /> },
    { value: `${prefix}-canView`, label: <FormattedMessage id="authkeys-permission-canview" /> },
    { value: `${prefix}-canEdit`, label: <FormattedMessage id="authkeys-permission-canedit" /> },
    { value: `${prefix}-canDelete`, label: <FormattedMessage id="authkeys-permission-candelete" /> },
    { value: `${prefix}-canRestoreDeleted`, label: <FormattedMessage id="authkeys-permission-canrestoredeleted" /> },
    { value: `${prefix}-canExport`, label: <FormattedMessage id="authkeys-permission-canexport" /> },
    { value: `${prefix}-canLock`, label: <FormattedMessage id="authkeys-permission-canlock" /> },
    { value: `${prefix}-canUnlock`, label: <FormattedMessage id="authkeys-permission-canunlock" /> },
    { value: `${prefix}-canArchive`, label: <FormattedMessage id="authkeys-permission-canarchive" /> },
    ...(includeCreateWorkOrder
        ? [{ value: `${prefix}-canCreateWorkOrder`, label: <FormattedMessage id="authkeys-permission-cancreateworkorder" /> }]
        : [])
];

const createSimpleBranch = (prefix, branchKey, labelId) => ({
    value: `${prefix}-${branchKey}`,
    label: <FormattedMessage id={labelId} />,
    children: [
        { value: `${prefix}-${branchKey}-canAdd`, label: <FormattedMessage id="authkeys-permission-canadd" /> },
        { value: `${prefix}-${branchKey}-canEdit`, label: <FormattedMessage id="authkeys-permission-canedit" /> },
        { value: `${prefix}-${branchKey}-canDelete`, label: <FormattedMessage id="authkeys-permission-candelete" /> }
    ]
});

const createTransitionBranch = (prefix, transitions = [], intl, transitionValuePrefix = prefix) => ({
    value: `${prefix}-transitions`,
    label: <FormattedMessage id="workflow-transition-plural" />,
    children: transitions?.map((trans) => ({
        value: `${transitionValuePrefix}-canApplyTransition-${trans?.name}`,
        label: (
            <FormattedMessage
                id="authkeys-permission-canapplytransition"
                values={{ transition: intl.formatMessage({ id: `workflow-transition-${trans?.name}` }).toLowerCase() }}
            />
        )
    }))
});

const createTaskBranch = (
    prefix,
    taskTransitions = [],
    intl,
    { ticketPrefix = `${prefix}-tasks-tickets`, followUpPrefix = `${prefix}-tasks-followups` } = {}
) => ({
    value: `${prefix}-tasks`,
    label: <FormattedMessage id="entity-workmgmt-task-plural" />,
    children: [
        { value: `${prefix}-tasks-canAdd`, label: <FormattedMessage id="authkeys-permission-canadd" /> },
        { value: `${prefix}-tasks-canEdit`, label: <FormattedMessage id="authkeys-permission-canedit" /> },
        { value: `${prefix}-tasks-canDelete`, label: <FormattedMessage id="authkeys-permission-candelete" /> },
        { value: `${prefix}-tasks-canRestoreDeleted`, label: <FormattedMessage id="authkeys-permission-canrestoredeleted" /> },
        createSimpleBranch(`${prefix}-tasks`, 'notes', 'entity-workmgmt-note-plural'),
        {
            value: ticketPrefix,
            label: <FormattedMessage id="entity-workmgmt-ticket-plural" />,
            children: [
                { value: `${ticketPrefix}-canAdd`, label: <FormattedMessage id="authkeys-permission-canadd" /> },
                { value: `${ticketPrefix}-canEdit`, label: <FormattedMessage id="authkeys-permission-canedit" /> },
                { value: `${ticketPrefix}-canDelete`, label: <FormattedMessage id="authkeys-permission-candelete" /> }
            ]
        },
        {
            value: followUpPrefix,
            label: <FormattedMessage id="entity-workmgmt-followup-plural" />,
            children: [
                { value: `${followUpPrefix}-canAdd`, label: <FormattedMessage id="authkeys-permission-canadd" /> },
                { value: `${followUpPrefix}-canEdit`, label: <FormattedMessage id="authkeys-permission-canedit" /> },
                { value: `${followUpPrefix}-canDelete`, label: <FormattedMessage id="authkeys-permission-candelete" /> }
            ]
        },
        createTransitionBranch(`${prefix}-tasks`, taskTransitions, intl, `${prefix}-tasks`)
    ]
});

const createTypePermissionNode = (typeNode, basePrefix, transitions, taskTransitions, intl, options = {}) => {
    const prefix = `${basePrefix}-${typeNode?.acronym?.toLowerCase()}`;
    const nestedTypeNodes = (typeNode?.children || []).map((childNode) =>
        createTypePermissionNode(childNode, basePrefix, transitions, taskTransitions, intl, options)
    );

    return {
        value: prefix,
        label: typeNode?.name,
        children: [
            ...createBasicPermissionChildren(prefix, options),
            createSimpleBranch(prefix, 'notes', 'entity-workmgmt-note-plural'),
            createSimpleBranch(prefix, 'files', 'entity-media-workmgmtfile-plural'),
            createTransitionBranch(prefix, transitions, intl),
            createTaskBranch(prefix, taskTransitions, intl),
            ...nestedTypeNodes
        ]
    };
};

export const buildBusinessOpportunityPermissionUpdates = ({ busOppTypeList = [], busOppTransitions = [], taskTransitions = [], intl }) => [
    {
        value: 'workmgmt-busopp',
        children: [
            {
                value: 'workmgmt-busopp-all',
                label: <FormattedMessage id="menuitems-workmgmt-busopp-all-title" />,
                children: [
                    ...createBasicPermissionChildren('workmgmt-busopp-all', { includeCreateWorkOrder: true }),
                    createSimpleBranch('workmgmt-busopp-all', 'notes', 'entity-workmgmt-note-plural'),
                    createSimpleBranch('workmgmt-busopp-all', 'files', 'entity-media-workmgmtfile-plural'),
                    createTransitionBranch('workmgmt-busopp-all', busOppTransitions, intl),
                    createTaskBranch('workmgmt-busopp-all', taskTransitions, intl)
                ]
            },
            ...buildEntityTypeHierarchy(busOppTypeList, 'businessOpportunityType').map((typeNode) =>
                createTypePermissionNode(typeNode, 'workmgmt-busopp', busOppTransitions, taskTransitions, intl, {
                    includeCreateWorkOrder: true
                })
            )
        ]
    }
];

export const buildWorkOrderPermissionUpdates = ({ workOrderTypeList = [], workOrderTransitions = [], taskTransitions = [], intl }) => [
    {
        value: 'workmgmt-workorder',
        children: [
            {
                value: 'workmgmt-workorder-all',
                label: <FormattedMessage id="menuitems-workmgmt-workorder-all-title" />,
                children: [
                    ...createBasicPermissionChildren('workmgmt-workorder-all'),
                    createSimpleBranch('workmgmt-workorder-all', 'notes', 'entity-workmgmt-note-plural'),
                    createSimpleBranch('workmgmt-workorder-all', 'files', 'entity-media-workmgmtfile-plural'),
                    createTransitionBranch('workmgmt-workorder-all', workOrderTransitions, intl),
                    createTaskBranch('workmgmt-workorder-all', taskTransitions, intl, {
                        ticketPrefix: 'workmgmt-workorder-all-tickets',
                        followUpPrefix: 'workmgmt-workorder-all-followups'
                    })
                ]
            },
            ...buildEntityTypeHierarchy(workOrderTypeList, 'workOrderType').map((typeNode) =>
                createTypePermissionNode(typeNode, 'workmgmt-workorder', workOrderTransitions, taskTransitions, intl)
            )
        ]
    }
];
